import { Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EMPTY_CELL_VALUE, EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { IStatusConfig, StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import ExperienceFeedbackService from '../../../services/ExperienceFeedback/ExperienceFeedbackService'
import IFilter, {
  IExperienceFeedback,
  IExperienceFeedbackResult
} from '../../../services/ExperienceFeedback/interfaces'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService } from '../../../services/User/UserService'
import ExperienceFeedbackViewPage from './ExperienceFeedbackViewPage'

/**
 * Veredito do backend: uma nota de aprovação vetada por um pilar crítico não é verde. O rótulo do
 * selo é a própria nota (`12 / 15`), então ele entra por `label`; do mapa vem só a cor.
 */
const EXPERIENCE_RESULT: Record<IExperienceFeedbackResult['key'], IStatusConfig> = {
  approved: { label: 'Aprovado', colorScheme: 'green' },
  critical_pillar: { label: 'Pilar crítico', colorScheme: 'yellow' },
  below: { label: 'Abaixo', colorScheme: 'red' }
}

/** `page`/`page_size` ficam de fora: são paginação, não filtro. */
const INITIAL_FILTER = {
  date: { start: '', end: '' },
  manager: '',
  name: '',
  sectorId: 0
}

export default function ExperienceFeedbackPage() {
  const modalState = useDisclosure()
  const [experienceFeedbackId, setExperienceFeedbackId] = useState<number>()
  const hasRole = useHasRole()
  const toast = useAppToast()
  const [deletingId, setDeletingId] = useState(0)
  const sectorService = new SectorService()
  const [sectors, setSectors] = useState<ISector[]>([])
  const [experienceFeedbacks, setExperienceFeedbacks] = useState<IExperienceFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const experienceFeedbackService = useMemo(() => new ExperienceFeedbackService(), [])
  const userService = new UserService()
  const [userSectorsIds, setUserSectorsIds] = useState<number[]>([])
  const permissions = {
    addExperienceFeedback: hasRole('add_experience_feedbacks'),
    deleteExperienceFeedback: hasRole('delete_experience_feedbacks'),
    downloadExperienceFeedback: hasRole('download_experience_feedbacks'),
    downloadSectorExperienceFeedback: hasRole('download_sector_experience_feedbacks')
  }
  const [totalPages, setTotalPages] = useState(1)
  const [filter, setFilter] = useState<IFilter>({ ...INITIAL_FILTER, page: 1, page_size: DEFAULT_PAGE_SIZE })
  const isDirty =
    filter.name !== '' ||
    filter.manager !== '' ||
    filter.sectorId !== 0 ||
    filter.date.start !== '' ||
    filter.date.end !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilter = (patch: Partial<IFilter>) => setFilter({ ...filter, ...patch, page: 1 })

  const fetchExperienceFeedbacks = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await experienceFeedbackService.list(filter)
      setExperienceFeedbacks(response.data)
      setTotalPages(Math.ceil(response.total_filtered / filter.page_size))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as avaliações de experiência', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSectors = async () => {
    try {
      const response = await sectorService.listForSelect()
      setSectors(response)
    } catch {
      toast.error('Erro ao buscar a lista de Setores!')
    }
  }

  const fetchUserSectors = () => {
    userService.getSectors().then((response) => {
      setUserSectorsIds(response.map((sectors) => sectors.id))
    })
  }

  const handleDelete = async () => {
    try {
      await experienceFeedbackService.delete(deletingId)
      fetchExperienceFeedbacks()

      toast.success('Avaliação deletada com sucesso!')
    } catch {
      toast.error('Erro ao deletar avaliação!')
    } finally {
      setDeletingId(0)
    }
  }

  useEffect(() => {
    fetchSectors()
    fetchUserSectors()
  }, [])

  // Debounce effect for filters
  // A busca por nome e gestor já chega debounced do `FilterSearch`.
  useEffect(() => {
    fetchExperienceFeedbacks()
  }, [filter])

  return (
    <>
      <PageTitle title="Avaliação de Experiência" description="Feedbacks de experiência registrados pelos gestores" />

      {permissions.addExperienceFeedback && (
        <ActionFab to="/experience-feedback/new" label="Novo feedback de experiência" openInNewTab />
      )}

      <FilterBar onClear={() => updateFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          label="Nome do liderado"
          value={filter.name}
          onChange={(name) => updateFilter({ name })}
          placeholder="Buscar por nome do liderado"
        />
        <FilterSelect
          label="Setor"
          value={filter.sectorId}
          onChange={(sectorId) => updateFilter({ sectorId: Number(sectorId) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          // A listagem trata `0` como "sem filtro de setor", não `''`.
          emptyValue={0}
        />
        <FilterSearch
          label="Gestor"
          value={filter.manager}
          onChange={(manager) => updateFilter({ manager })}
          placeholder="Buscar por gestor"
        />
        <FilterDateRange label="Data" value={filter.date} onChange={(date) => updateFilter({ date })} />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome do liderado</Th>
              <Th>Gestor</Th>
              <Th>Setor</Th>
              <Th>Pontuação</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="feedbacks" onRetry={fetchExperienceFeedbacks} />
            ) : experienceFeedbacks.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="feedback" />
            ) : (
              experienceFeedbacks.map((experienceFeedback) => (
                <Tr key={experienceFeedback.id}>
                  <Td>{experienceFeedback.id}</Td>
                  <Td>{experienceFeedback.name}</Td>
                  <Td>{experienceFeedback.manager}</Td>
                  <Td>{sectors.find((sector) => sector.id === experienceFeedback.sector_id)?.name}</Td>
                  <Td>
                    {experienceFeedback.result == null ? (
                      <EmptyCell />
                    ) : (
                      <Tooltip
                        isDisabled={!experienceFeedback.grades?.length}
                        label={experienceFeedback.grades?.map((grade) => (
                          <Text key={grade.slug}>
                            {grade.title}: <strong>{grade.grade ?? EMPTY_CELL_VALUE}</strong>
                          </Text>
                        ))}
                      >
                        <StatusBadge
                          status={experienceFeedback.result.key}
                          statuses={EXPERIENCE_RESULT}
                          label={`${experienceFeedback.score} / ${experienceFeedback.max_score}`}
                        />
                      </Tooltip>
                    )}
                  </Td>
                  <Td>{formatDateTime(experienceFeedback.created_at)}</Td>
                  <Td>
                    {experienceFeedback.step == 0 && (
                      <>
                        <ActionButton
                          action="view"
                          onClick={() => {
                            modalState.onOpen()
                            setExperienceFeedbackId(experienceFeedback.id)
                          }}
                        />
                        {(permissions.downloadExperienceFeedback ||
                          (userSectorsIds.includes(experienceFeedback.sector_id) &&
                            permissions.downloadSectorExperienceFeedback)) && (
                          <a
                            href={experienceFeedback.link_pdf}
                            download="Avaliação de Experiência.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ActionButton action="download" />
                          </a>
                        )}
                      </>
                    )}

                    {experienceFeedback.step != 0 && permissions.addExperienceFeedback && (
                      <ActionButton action="edit" to={`/experience-feedback/${experienceFeedback.id}`} />
                    )}
                    {permissions.deleteExperienceFeedback && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeletingId(experienceFeedback.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setFilter({ ...filter, page })
        }}
        pageSize={filter.page_size}
        onPageSizeChange={(pageSize) => {
          setFilter({ ...filter, page_size: pageSize, page: 1 })
        }}
      />

      {/* scrollBehavior="inside": a avaliação tem 7 perguntas + o bloco de resultado, e no
          default ("outside") o modal inteiro rola pra fora da tela em vez de rolar o corpo. */}
      <FormModal isOpen={modalState.isOpen} onClose={modalState.onClose} size="wide" title="Avaliação de Experiência">
        {experienceFeedbackId !== undefined && (
          <ExperienceFeedbackViewPage experienceFeedbackId={experienceFeedbackId} sectors={sectors} />
        )}
      </FormModal>

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => setDeletingId(0)}
        onConfirm={handleDelete}
        action="delete"
        entity="avaliação de experiência"
        feminine
      />
    </>
  )
}
